import { Fragment } from 'react';

type Segment = { kind: 'text'; text: string } | { kind: 'link'; text: string; href: string };

const LINK = /\bhttps?:\/\/[^\s<>"']+/gi;
const TRAILING = /[.,;:!?)\]}»]+$/;

/**
 * A message's text with its web addresses as links opening in a new tab. Only
 * http and https become links. The creator portal cuts text the same way
 * (`affiliate-portal/src/lib/creator-messages/linkify.ts`); two apps, no
 * shared package for a dozen lines.
 */
export function linkify(text: string): Segment[] {
  const segments: Segment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(LINK)) {
    const start = match.index ?? 0;
    const candidate = match[0].replace(TRAILING, '');
    let href: string | null = null;
    try {
      const url = new URL(candidate);
      href = url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
    } catch {
      href = null;
    }
    if (!href) continue;
    if (start > cursor) segments.push({ kind: 'text', text: text.slice(cursor, start) });
    segments.push({ kind: 'link', text: candidate, href });
    cursor = start + candidate.length;
  }
  if (cursor < text.length) segments.push({ kind: 'text', text: text.slice(cursor) });
  return segments;
}

export function LinkedText({ text }: { text: string }) {
  return (
    <>
      {linkify(text).map((segment, index) =>
        segment.kind === 'link' ? (
          <a
            key={index}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}
          >
            {segment.text}
          </a>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        )
      )}
    </>
  );
}

/** The draft a creator's message was sent as (a link for feedback), if any. */
export function readMessageDraft(
  metadata: unknown
): { format: string; version: number; link: string } | null {
  const draft = (metadata as { draft?: Record<string, unknown> } | null)?.draft;
  if (
    !draft ||
    typeof draft.format !== 'string' ||
    typeof draft.version !== 'number' ||
    typeof draft.link !== 'string'
  ) {
    return null;
  }
  return { format: draft.format, version: draft.version, link: draft.link };
}

/**
 * The UGC version a creator's message is about, frozen when she sent it:
 * « À propos de Vidéo 2 · V1 ». Null when the message names none.
 */
export function readMessageAbout(metadata: unknown): {
  format: string;
  unitIndex: number;
  versionNumber: number;
  deliverableId: string | null;
  deliverableVersionId: string | null;
  /** Null on a message written before the collab was named with it: nothing to open. */
  collabId: string | null;
} | null {
  const about = (metadata as { about?: Record<string, unknown> } | null)?.about;
  if (
    !about ||
    typeof about.format !== 'string' ||
    typeof about.unitIndex !== 'number' ||
    typeof about.versionNumber !== 'number'
  ) {
    return null;
  }
  const text = (value: unknown) => (typeof value === 'string' && value ? value : null);
  const deliverableId = text(about.deliverableId);
  return {
    format: about.format,
    unitIndex: about.unitIndex,
    versionNumber: about.versionNumber,
    deliverableId,
    deliverableVersionId: text(about.deliverableVersionId),
    collabId: deliverableId ? text(about.collabId) : null,
  };
}
